/**
 * Best-By Date Picker Component (Dropdown Version)
 * 
 * Handles customer selection of best-by dates from a dropdown and updates:
 * - Displayed price
 * - Line item properties (for cart/checkout)
 * - Add to cart button availability
 */

(function() {
  'use strict';
  
  if (!customElements.get('best-by-date-picker')) {
    class BestByDatePicker extends HTMLElement {
      constructor() {
        super();
        try {
          this.select = this.querySelector('select');
          
          // Find product form - try multiple methods
          const formId = this.dataset.productFormId;
          if (formId) {
            this.productForm = document.querySelector(`#${formId}`);
          }
          
          // Fallback: try to find form by class or other selectors
          if (!this.productForm) {
            // Try finding by class
            this.productForm = document.querySelector('.js-product-form') || 
                              document.querySelector('.js-product-form-main') ||
                              document.querySelector('form[action*="/cart/add"]') ||
                              this.closest('product-form')?.querySelector('form');
          }
          
          // Debug form finding
          if (!this.productForm) {
            console.warn('BestByDatePicker: Could not find product form. Tried:', {
              formId: formId,
              byId: formId ? document.querySelector(`#${formId}`) : null,
              byClass: document.querySelector('.js-product-form'),
              byAction: document.querySelector('form[action*="/cart/add"]')
            });
          } else {
            console.log('BestByDatePicker: Found product form:', this.productForm.id || this.productForm.className);
          }
          
          // Find price container - try multiple selectors for compatibility
          this.priceContainer = document.querySelector('.product-info__price > .price') || 
                               document.querySelector('.product-info__price .price');
          this.priceDisplay = this.priceContainer?.querySelector('.price__current');
          this.priceWas = this.priceContainer?.querySelector('.price__was');
          this.priceDefault = this.priceContainer?.querySelector('.price__default');
          this.addToCartBtn = this.productForm?.querySelector('button[type="submit"]');
          
          // Get original price from product data or current display
          this.originalPrice = this.getOriginalPrice();
          
          // Initialize
          this.init();
        } catch (error) {
          console.error('BestByDatePicker constructor error:', error);
        }
      }
      
      findProductForm() {
        // Try multiple methods to find the product form
        const formId = this.dataset.productFormId;
        
        if (formId) {
          this.productForm = document.querySelector(`#${formId}`);
        }
        
        // Fallback: try to find form by class or other selectors
        if (!this.productForm) {
          // Try finding by class
          this.productForm = document.querySelector('.js-product-form') || 
                            document.querySelector('.js-product-form-main') ||
                            document.querySelector('form[action*="/cart/add"]') ||
                            this.closest('product-form')?.querySelector('form');
        }
        
        // If still not found, try looking within the same section
        if (!this.productForm) {
          const section = this.closest('.shopify-section');
          if (section) {
            this.productForm = section.querySelector('form.js-product-form') ||
                             section.querySelector('form[action*="/cart/add"]');
          }
        }
        
        if (this.productForm) {
          console.log('BestByDatePicker: Found product form:', this.productForm.id || this.productForm.className);
          // Update addToCartBtn reference
          this.addToCartBtn = this.productForm.querySelector('button[type="submit"]');
        } else {
          console.warn('BestByDatePicker: Could not find product form. Tried:', {
            formId: formId,
            byId: formId ? document.querySelector(`#${formId}`) : null,
            byClass: document.querySelector('.js-product-form'),
            byAction: document.querySelector('form[action*="/cart/add"]')
          });
        }
        
        return this.productForm;
      }
      
      getOriginalPrice() {
        // First, try to get from data attribute (most reliable - set by Liquid template, already in cents)
        if (this.dataset.originalPrice) {
          const priceInCents = parseInt(this.dataset.originalPrice);
          if (!isNaN(priceInCents)) {
            console.log('BestByDatePicker: Got original price from data attribute:', priceInCents, 'cents');
            return priceInCents;
          }
        }
        
        // Fallback: try to get price from variant-picker JSON data (already in cents)
        try {
          const variantPicker = document.querySelector('variant-picker');
          if (variantPicker) {
            const variantPickerData = variantPicker.querySelector('script[type="application/json"]');
            if (variantPickerData) {
              const data = JSON.parse(variantPickerData.textContent);
              if (data && data.product && data.product.variants && data.product.variants.length > 0) {
                // Get price from first variant (already in cents)
                const variantPrice = data.product.variants[0].price;
                if (variantPrice) {
                  const priceInCents = parseInt(variantPrice);
                  console.log('BestByDatePicker: Got original price from variant-picker:', priceInCents, 'cents');
                  return priceInCents;
                }
              }
            }
          }
        } catch (e) {
          console.log('BestByDatePicker: Could not get price from variant-picker, trying element extraction', e);
        }
        
        // Last resort: extract from displayed price element
        if (this.priceDisplay) {
          const extracted = this.getPriceFromElement(this.priceDisplay);
          console.log('BestByDatePicker: Got original price from element:', extracted, 'cents');
          return extracted;
        }
        
        console.log('BestByDatePicker: Could not get original price');
        return null;
      }
      
      getPriceFromElement(element) {
        // Extract price from element (handles both textContent and innerHTML with HTML entities)
        const text = element.textContent || element.innerText || '';
        // Remove currency symbols, spaces, and parse
        const cleaned = text.replace(/[^0-9.]/g, '');
        const dollars = parseFloat(cleaned);
        
        if (isNaN(dollars)) return null;
        
        // Prices displayed on page are in dollars (e.g., "5.59" for $5.59)
        // Convert to cents
        return Math.round(dollars * 100);
      }
      
      init() {
        try {
          if (!this.select) return;
          
          // Try to find form again if it wasn't found initially (might load after custom element)
          if (!this.productForm) {
            this.findProductForm();
          }
          
          // Ensure first non-disabled option is selected
          this.selectFirstAvailableOption();
          
          // Capture original price from the page if not already captured
          if (!this.originalPrice && this.priceDisplay) {
            this.originalPrice = this.getPriceFromElement(this.priceDisplay);
          }
          
          // Set up event listener
          this.select.addEventListener('change', this.handleChange.bind(this));
          
          // Set initial state - this ensures properties are set even if user doesn't change selection
          const selectedOption = this.select.options[this.select.selectedIndex];
          if (selectedOption && !selectedOption.disabled) {
            this.handleChange({ target: this.select });
          }
          
          // Also listen for form submission to ensure properties are set
          if (this.productForm) {
            // Use capture phase to run BEFORE the product-form.js handler
            this.productForm.addEventListener('submit', (e) => {
              console.log('BestByDatePicker: Form submit event captured');
              
              // Double-check that properties are set before form submission
              const selectedOption = this.select.options[this.select.selectedIndex];
              if (selectedOption && !selectedOption.disabled) {
                const price = parseInt(selectedOption.dataset.price) || 0;
                const date = selectedOption.dataset.date;
                const value = selectedOption.value;
                
                console.log('BestByDatePicker: Form submit - selected option:', {
                  value: value,
                  date: date,
                  price: price
                });
                
                // Force update properties right before submission
                this.updateLineItemProperty(value, date, price);
                
                // Verify property is in form before submission
                const verifyInput = this.productForm.querySelector('input[name="properties[Best By Date]"]');
                if (!verifyInput) {
                  console.error('BestByDatePicker: Property missing on form submit! Re-adding...');
                  // Try one more time
                  this.updateLineItemProperty(value, date, price);
                  
                  // Check again
                  const verifyInput2 = this.productForm.querySelector('input[name="properties[Best By Date]"]');
                  if (!verifyInput2) {
                    console.error('BestByDatePicker: CRITICAL - Property still missing after re-add!');
                    // Last resort: manually create and append
                    const manualInput = document.createElement('input');
                    manualInput.type = 'hidden';
                    manualInput.name = 'properties[Best By Date]';
                    manualInput.value = this.formatDate(date);
                    this.productForm.appendChild(manualInput);
                    console.log('BestByDatePicker: Manually added property:', manualInput.name, '=', manualInput.value);
                  }
                } else {
                  console.log('BestByDatePicker: Property verified on form submit:', verifyInput.name, '=', verifyInput.value);
                  
                  // Also log all properties in the form for debugging
                  const allProps = this.productForm.querySelectorAll('input[name^="properties["]');
                  console.log('BestByDatePicker: All properties in form:', Array.from(allProps).map(input => ({
                    name: input.name,
                    value: input.value
                  })));
                  
                  // Also verify the property will be in FormData
                  const formData = new FormData(this.productForm);
                  const propertyValue = formData.get('properties[Best By Date]');
                  console.log('BestByDatePicker: Property in FormData:', propertyValue);
                  
                  if (!propertyValue) {
                    console.error('BestByDatePicker: WARNING - Property not found in FormData! This means it won\'t be submitted.');
                  }
                }
              } else {
                console.warn('BestByDatePicker: No valid option selected on form submit');
              }
            }, true); // Use capture phase (true) to run before other handlers
          }
        } catch (error) {
          console.error('BestByDatePicker init error:', error);
        }
      }
      
      selectFirstAvailableOption() {
        try {
          if (!this.select) return;
          
          console.log('BestByDatePicker: Selecting first available option. Current selectedIndex:', this.select.selectedIndex);
          
          // Find first non-disabled option
          for (let i = 0; i < this.select.options.length; i++) {
            const option = this.select.options[i];
            console.log(`BestByDatePicker: Checking option ${i}:`, {
              value: option.value,
              date: option.dataset.date,
              disabled: option.disabled
            });
            
            if (!option.disabled) {
              this.select.selectedIndex = i;
              console.log('BestByDatePicker: Selected option', i, 'with value', option.value, 'and date', option.dataset.date);
              return;
            }
          }
          
          // If all options are disabled, select the first one anyway (fallback)
          if (this.select.options.length > 0) {
            console.warn('BestByDatePicker: All options disabled, selecting first as fallback');
            this.select.selectedIndex = 0;
          }
        } catch (error) {
          console.error('BestByDatePicker selectFirstAvailableOption error:', error);
        }
      }
    
      handleChange(evt) {
        try {
          const select = evt.target || this.select;
          const selectedIndex = select.selectedIndex;
          const selectedOption = select.options[selectedIndex];
          
          console.log('BestByDatePicker handleChange:', {
            selectedIndex: selectedIndex,
            selectedValue: selectedOption?.value,
            selectedDate: selectedOption?.dataset.date,
            isDisabled: selectedOption?.disabled,
            allOptions: Array.from(select.options).map((opt, idx) => ({
              index: idx,
              value: opt.value,
              date: opt.dataset.date,
              disabled: opt.disabled,
              selected: opt.selected
            }))
          });
          
          if (!selectedOption) {
            console.warn('BestByDatePicker: No option selected');
            return;
          }
          
          // Prevent selection of disabled options
          if (selectedOption.disabled) {
            console.warn('BestByDatePicker: Selected option is disabled, reverting to first available');
            // Revert to first available option
            this.selectFirstAvailableOption();
            return;
          }
          
          const price = parseInt(selectedOption.dataset.price) || 0;
          const date = selectedOption.dataset.date;
          const quantity = parseInt(selectedOption.dataset.quantity) || 0;
          const value = selectedOption.value; // "primary", "secondary", or "tertiary"
          
          console.log('BestByDatePicker: Processing selection:', {
            value: value,
            date: date,
            price: price,
            quantity: quantity
          });
          
          // Update displayed price
          this.updatePrice(price);
          
          // Update line item properties (including price for potential adjustment)
          this.updateLineItemProperty(value, date, price);
          
          // Update availability
          this.updateAvailability(quantity);
          
          // Dispatch custom event (for other components to listen, including quantity limit handler)
          this.dispatchEvent(new CustomEvent('best-by-date:change', {
            detail: {
              value: value,
              date: date,
              price: price,
              quantity: quantity
            },
            bubbles: true
          }));
          
          // Also dispatch a generic 'change' event for the quantity limit handler
          this.dispatchEvent(new CustomEvent('change', { bubbles: true }));
        } catch (error) {
          console.error('BestByDatePicker handleChange error:', error);
        }
      }
    
      updatePrice(price) {
        try {
          if (!this.priceDisplay || !price) return;
          
          console.log('BestByDatePicker updatePrice:', {
            newPrice: price,
            originalPrice: this.originalPrice,
            newPriceFormatted: this.formatPrice(price),
            originalPriceFormatted: this.originalPrice ? this.formatPrice(this.originalPrice) : 'N/A'
          });
          
          // Format price (price is in cents)
          const formattedPrice = this.formatPrice(price);
          
          // Update price container classes
          if (this.priceContainer) {
            // Check if this is a sale price (lower than original)
            // Both prices should already be in cents (from getOriginalPrice() and selectedOption.dataset.price)
            const isOnSale = this.originalPrice && price < this.originalPrice;
            this.priceContainer.classList.toggle('price--on-sale', isOnSale);
            
            // Update compare_at_price (was price) if on sale
            // Match theme's structure: original price in <s class="price__was"> tag
            if (this.priceWas) {
              if (isOnSale && this.originalPrice) {
                const originalFormatted = this.formatPrice(this.originalPrice);
                console.log('BestByDatePicker: Setting original price (was):', originalFormatted);
                // Set innerHTML to just the price (the <s> tag is already in the HTML)
                // Match theme's format: just the price, no extra labels
                this.priceWas.innerHTML = originalFormatted;
              } else {
                // Clear the was price if not on sale
                this.priceWas.innerHTML = '';
              }
            }
            
            // Update current price AFTER setting the was price
            // This ensures proper display order
            this.priceDisplay.innerHTML = formattedPrice;
          }
        } catch (error) {
          console.error('BestByDatePicker updatePrice error:', error);
        }
      }
      
      getSaleLabel() {
        // Get sale label from theme translations if available
        try {
          const saleLabel = document.querySelector('[data-sale-label]')?.dataset.saleLabel;
          return saleLabel || 'Was';
        } catch {
          return 'Was';
        }
      }
      
      updateLineItemProperty(value, date, price) {
        try {
          if (!this.productForm) {
            console.warn('BestByDatePicker: productForm not found');
            return;
          }
          
          // Remove existing properties (including all variations of the name)
          const existing = this.productForm.querySelectorAll(
            'input[name*="best_by_date"], input[name*="Best By Date"], input[name*="_custom_price"], input[name*="properties["]'
          );
          existing.forEach(input => {
            // Only remove our properties, not all properties
            const name = input.name || '';
            if (name.includes('best_by_date') || name.includes('Best By Date') || name.includes('_custom_price')) {
              input.remove();
            }
          });
          
          // Add property for display (human-readable) - this is what shows in Shopify admin
          // IMPORTANT: Property name must NOT start with underscore to be visible in admin
          const displayInput = document.createElement('input');
          displayInput.type = 'hidden';
          displayInput.name = 'properties[Best By Date]';
          displayInput.value = this.formatDate(date);
          this.productForm.appendChild(displayInput);
          
          // Add new property for the date level (primary/secondary/tertiary) - hidden property
          const levelInput = document.createElement('input');
          levelInput.type = 'hidden';
          levelInput.name = 'properties[_best_by_date]';
          levelInput.value = value;
          this.productForm.appendChild(levelInput);
          
          // Add property for the actual date value (raw) - hidden property
          const dateInput = document.createElement('input');
          dateInput.type = 'hidden';
          dateInput.name = 'properties[_best_by_date_value]';
          dateInput.value = date;
          this.productForm.appendChild(dateInput);
          
          // Add custom price property (for potential price adjustment scripts) - hidden property
          const priceInput = document.createElement('input');
          priceInput.type = 'hidden';
          priceInput.name = 'properties[_custom_price]';
          priceInput.value = price; // Price in cents
          this.productForm.appendChild(priceInput);
          
          // Also add as a data attribute on the form for easy access
          this.productForm.dataset.customPrice = price;
          
          // Debug: Log the properties being set
          console.log('BestByDatePicker: Set properties:', {
            'Best By Date': this.formatDate(date),
            '_best_by_date': value,
            '_best_by_date_value': date,
            '_custom_price': price
          });
          
          // Verify the property is in the form
          const verifyInput = this.productForm.querySelector('input[name="properties[Best By Date]"]');
          if (verifyInput) {
            console.log('BestByDatePicker: Verified property in form:', verifyInput.name, '=', verifyInput.value);
          } else {
            console.error('BestByDatePicker: Property not found in form after setting!');
          }
        } catch (error) {
          console.error('BestByDatePicker updateLineItemProperty error:', error);
        }
      }
      
      updateAvailability(quantity) {
        try {
          if (!this.addToCartBtn) return;
          
          if (quantity <= 0) {
            this.addToCartBtn.disabled = true;
            this.addToCartBtn.setAttribute('aria-disabled', 'true');
            // Update button text if needed
            const soldOutText = this.addToCartBtn.dataset.soldOutText || 'Sold Out';
            if (this.addToCartBtn.dataset.originalText === undefined) {
              this.addToCartBtn.dataset.originalText = this.addToCartBtn.textContent.trim();
            }
            this.addToCartBtn.textContent = soldOutText;
          } else {
            this.addToCartBtn.disabled = false;
            this.addToCartBtn.removeAttribute('aria-disabled');
            // Restore original text
            if (this.addToCartBtn.dataset.originalText) {
              this.addToCartBtn.textContent = this.addToCartBtn.dataset.originalText;
            }
          }
        } catch (error) {
          console.error('BestByDatePicker updateAvailability error:', error);
        }
      }
      
      formatPrice(priceInCents) {
        try {
          // Convert cents to dollars and format
          const dollars = (priceInCents / 100).toFixed(2);
          // Format with currency symbol (adjust as needed)
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(dollars);
        } catch (error) {
          console.error('BestByDatePicker formatPrice error:', error);
          return '$0.00';
        }
      }
      
      formatDate(dateString) {
        try {
          // Format date string (YYYY-MM-DD) to readable format
          // Match the format used in the dropdown: "June 01, 2026"
          // Parse the date string directly to avoid timezone issues
          const parts = dateString.split('-');
          if (parts.length !== 3) {
            // Fallback to Date object if format is unexpected
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = date.toLocaleDateString('en-US', { month: 'long' });
            const day = String(date.getDate()).padStart(2, '0');
            return `${month} ${day}, ${year}`;
          }
          
          const year = parseInt(parts[0], 10);
          const monthIndex = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const day = parts[2];
          
          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                             'July', 'August', 'September', 'October', 'November', 'December'];
          const month = monthNames[monthIndex];
          
          return `${month} ${day}, ${year}`;
        } catch (error) {
          console.error('BestByDatePicker formatDate error:', error);
          return dateString;
        }
      }
    }
    
    try {
      customElements.define('best-by-date-picker', BestByDatePicker);
    } catch (error) {
      console.error('Failed to define best-by-date-picker custom element:', error);
    }
  }
})();
